import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    DocumentInterruptSource,
    Idle,
    StorageInterruptSource,
    type EventTargetInterruptOptions,
} from '@ng-idle/core';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SessionIdleService {
    // --- Dependency injection ---

    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly idle = inject(Idle);
    private readonly destroyRef = inject(DestroyRef);

    private readonly _sessionIdled$ = new Subject<void>();
    public readonly sessionIdled$ = this._sessionIdled$.asObservable();

    private readonly _sessionActivated$ = new Subject<void>();
    public readonly sessionActivated$ = this._sessionActivated$.asObservable();

    private readonly _sessionTimingOut$ = new Subject<number>();
    public readonly sessionTimingOut$ = this._sessionTimingOut$.asObservable();

    private readonly _sessionTimedOut$ = new Subject<void>();
    public readonly sessionTimedOut$ = this._sessionTimedOut$.asObservable();

    // --- Lifecycle ---

    constructor() {
        this.setupParameters();
        this.onIdleEvents();
    }

    // --- Public methods ---

    /** reset the idle process */
    public startIdleProcess() {
        this.idle.watch();
    }

    // --- Private methods ---

    /** set idle parameters */
    private setupParameters() {
        // how long can they be inactive before considered idle, in seconds
        this.idle.setIdle(this.appConfig.sessionConfig.inactiveTimeoutSeconds);
        // how long can they be idle before considered timed out, in seconds
        this.idle.setTimeout(this.appConfig.sessionConfig.idleTimeoutSeconds);
        // provide sources that will "interrupt" aka provide events indicating the user is active
        this.idle.setInterrupts(this.createCustomInterruptSources());
    }

    private onIdleEvents() {
        // do something when the user becomes idle
        this.idle.onIdleStart
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this._sessionIdled$.next();
            });

        // do something when the user is no longer idle
        this.idle.onIdleEnd
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this._sessionActivated$.next();
                this.startIdleProcess();
            });

        // do something as the timeout countdown does its thing
        this.idle.onTimeoutWarning
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((seconds) => this._sessionTimingOut$.next(seconds));

        // do something when the user has timed out
        this.idle.onTimeout
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this._sessionTimedOut$.next());
    }

    private createCustomInterruptSources(
        options?: number | EventTargetInterruptOptions
    ) {
        return [
            new DocumentInterruptSource(
                'keydown mousedown touchstart touchmove scroll',
                options
            ),
            new StorageInterruptSource(options),
        ];
    }
}
